import { useEffect, useState } from "react";
import { Loader2, Check, X, Trash2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { StarRating } from "@/components/store/StarRating";
import { formatDate } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";
import api, { apiError } from "@/lib/api";

const TABS = [
  { value: "pending", es: "Pendientes", en: "Pending" },
  { value: "approved", es: "Aprobadas", en: "Approved" },
  { value: "rejected", es: "Rechazadas", en: "Rejected" },
];

export default function AdminReviews() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/reviews").then((r) => setReviews(r.data.reviews)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const moderate = async (r, moderation) => {
    setBusy(r.id);
    try {
      const { data } = await api.put(`/admin/reviews/${r.id}`, { moderation });
      setReviews((prev) => prev.map((x) => (x.id === r.id ? data.review : x)));
      toast.success(
        moderation === "approved" ? (es ? "Reseña aprobada" : "Review approved")
          : (es ? "Reseña rechazada" : "Review rejected"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((x) => x.id !== id));
      toast.success(es ? "Reseña eliminada" : "Review deleted");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const moderationOf = (r) => r.moderation || (r.approved ? "approved" : "pending");
  const counts = TABS.reduce((acc, t) => {
    acc[t.value] = reviews.filter((r) => moderationOf(r) === t.value).length;
    return acc;
  }, {});
  const list = reviews.filter((r) => moderationOf(r) === tab);

  return (
    <div data-testid="admin-reviews">
      <div className="mb-8">
        <h1 className="font-serif text-4xl font-light">{es ? "Reseñas" : "Reviews"}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {es ? "Aprueba o rechaza las reseñas antes de publicarlas" : "Approve or reject reviews before they go live"}
        </p>
      </div>

      <div className="flex gap-1 border-b border-border mb-6">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            data-testid={`reviews-tab-${t.value}`}
            className={`px-5 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === t.value ? "border-gold text-gold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            {t[lang]}
            <span className="ml-2 text-xs px-1.5 py-0.5 bg-secondary rounded-full" data-testid={`reviews-count-${t.value}`}>{counts[t.value]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
      ) : list.length === 0 ? (
        <div className="py-20 text-center text-muted-foreground border border-border bg-card" data-testid="reviews-empty">
          <MessageSquare className="h-8 w-8 mx-auto mb-3 opacity-40" />
          {tab === "pending" ? (es ? "No hay reseñas pendientes." : "No pending reviews.")
            : tab === "approved" ? (es ? "No hay reseñas aprobadas." : "No approved reviews.")
              : (es ? "No hay reseñas rechazadas." : "No rejected reviews.")}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          {list.map((r) => (
            <div key={r.id} className="border border-border bg-card p-6 flex flex-col" data-testid={`review-card-${r.id}`}>
              <div className="flex items-center justify-between">
                <StarRating rating={r.rating} />
                <span className="text-xs text-muted-foreground">{formatDate(r.created_at)}</span>
              </div>
              <p className="font-medium mt-3" data-testid={`review-name-${r.id}`}>{r.customer_name}</p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1" data-testid={`review-text-${r.id}`}>{r.text}</p>

              <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-border">
                {tab !== "approved" && (
                  <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy === r.id} onClick={() => moderate(r, "approved")} data-testid={`approve-review-${r.id}`}>
                    <Check className="h-4 w-4 mr-1" /> {es ? "Aprobar" : "Approve"}
                  </Button>
                )}
                {tab !== "rejected" && (
                  <Button size="sm" variant="outline" className="rounded-none" disabled={busy === r.id} onClick={() => moderate(r, "rejected")} data-testid={`reject-review-${r.id}`}>
                    <X className="h-4 w-4 mr-1" /> {es ? "Rechazar" : "Reject"}
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="rounded-none hover:text-destructive ml-auto" onClick={() => setDeleteId(r.id)} data-testid={`delete-review-${r.id}`}>
                  <Trash2 className="h-4 w-4 mr-1" /> {es ? "Eliminar" : "Delete"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>{es ? "¿Eliminar esta reseña?" : "Delete this review?"}</AlertDialogTitle>
            <AlertDialogDescription>{es ? "Esta acción no se puede deshacer." : "This action cannot be undone."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{es ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-review">{es ? "Eliminar" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
